




















































import { classToPlain } from 'class-transformer'
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { CSVModelInterface } from './CSVModel.interface'
import { csvParser } from './CSVParser'
import AppDatatable from '@core/components/AppDatatable.vue'
import AppDialog from '@core/components/AppDialog.vue'
import { TemplateDownloadService } from '@core/pages/ifamFileSystem/api/services/TemplateDownloadService'
import { UIService } from '@core/services/UIService'
import { SharedVariable } from '@core/model/SharedVariable'
import { inject } from 'inversify-props'
import { base64ToBlob } from '@core/tools/documents'

@Component({
  components: {
    AppDatatable,
    AppDialog
  }
})
export default class CSVImport extends Vue {
  @Prop({ required: true, type: Function })
  private readonly CSVModel!: any

  @Prop({ required: true })
  private readonly isImporting!: boolean

  @Prop()
  private readonly validationErrors: Array<string[]>

  @Prop()
  private readonly successfulImportRows: Array<string>

  @Prop({ default: false })
  private readonly resetCSV: boolean

  uiService: UIService

  @inject()
  private sharedVariable: SharedVariable

  private imported: CSVModelInterface[] = []
  private selectedFile = ''
  private loading = false
  private txtSearchTop = ''
  private csvErrorModal = false
  private csvErrors: string[] = []

  get canImport (): boolean {
    if (!this.imported.length) {
      return false
    }

    return !this.tableRow.some(e => e.error)
  }

  get errors (): string[] {
    const validationErrors =
      this.validationErrors && this.validationErrors.length > 0
        ? this.validationErrors?.map((errors, i) => errors?.filter(error => !!error)?.map(error => `${error} at Row ${i + 1}`))
        : []
    return [...validationErrors.flat().filter(val => !!val), ...this.csvErrors]
  }

  get successfulRows (): string[] {
    return this.successfulImportRows?.map((row, i) => {
      return row !== null ? `Imported Row ${i + 1}` : null
    }).filter(val => !!val)
  }

  async downloadTemplate () {
    const useStaticTemplate = this.CSVModel.usedStaticTemplate()
    if (useStaticTemplate) {
      if (!useStaticTemplate.templateCategory) {
        await this.uiService.alert.show({
          type: 'warning',
          message: 'The template category is empty.'
        })
        return
      }
      const data = await TemplateDownloadService.downloadTemplate(useStaticTemplate.templateCategory)
      if (data.error) {
        await this.uiService.alert.show({
          type: 'warning',
          message: data.error
        })
        return
      }
      // if the template is a static file
      const link = document.createElement('a')
      link.href = base64ToBlob(data.fileData, useStaticTemplate.fileType)
      link.setAttribute('download', useStaticTemplate.downloadedFileName)
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      this.CSVModel.example()
    }
  }

  get tableColumn () {
    const obj = new this.CSVModel()
    const plain = classToPlain(obj, {
      strategy: 'excludeAll',
      excludeExtraneousValues: true,
      enableImplicitConversion: true
    })

    return ['Row', ...Object.keys(plain)]?.map(e => {
      return {
        label: e,
        field: e
      }
    })
  }

  get tableRow () {
    if (!this.imported || this.imported.length <= 0) {
      return []
    }
    return this.imported?.map((e, i) => {
      const obj = classToPlain((e), {
        strategy: 'excludeAll',
        excludeExtraneousValues: true,
        enableImplicitConversion: true
      })

      const validationErrors = this.validationErrors && this.validationErrors.length > 0
      const hasValidationErrors = (validationErrors && this.validationErrors[i]?.length > 0) || false

      return {
        Row: i + 1,
        ...obj,
        error: (e.error || hasValidationErrors)
      }
    })
  }

  async fileChanges (files: Array<any>) {
    try {
      const file = files[0]
      this.selectedFile = file.name
      const { success, items, errors } = await csvParser(this.CSVModel, file)
      this.imported = items
      if (!success) {
        throw errors
      }
    } catch (error) {
      this.csvErrors = error
      this.csvErrorModal = true
    }
    const csvFile = this.$refs.csvFile as any
    csvFile.value = ''
    this.$emit('csvUpload', this.imported)
  }

  importCsv () {
    this.$emit('importCsv', this.imported)
  }

  mounted () {
    this.uiService = this.sharedVariable.uiService
  }

  @Watch('resetCSV')
  onResetCSV () {
    const csvFile = this.$refs.csvFile as any
    csvFile.value = ''
    this.imported = []
  }

  @Watch('validationErrors')
  errorsOnChange () {
    if (this.validationErrors && this.validationErrors.some(e => e && e.length > 0)) {
      this.csvErrorModal = true
    }
  }

  @Watch('csvErrorModal')
  csvErrorModalOnChange (val: boolean) {
    if (!val) {
      this.csvErrors = []
    }
  }
}
